import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  User
} from 'lucide-react';

import { Candidate, TimelineEntry } from '@/types/models';
import { candidateService } from '@/services/candidate.service';

interface CandidateTimelineDialogProps {
  candidate: Candidate | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onTimelineUpdate?: (timeline: TimelineEntry[]) => void;
}

const CandidateTimelineDialog: React.FC<CandidateTimelineDialogProps> = ({
  candidate,
  open,
  onOpenChange }) => {
  if (!candidate) return null;

  const timelineEvents = [...(candidate.timeline || [])].reverse();

  const getStatusString = (status: any): string => {
    if (!status) return '';
    if (typeof status === 'object' && 'name' in status) return String(status.name);
    return String(status);
  };

  const formatStatusString = (status: string): string => {
    if (!status) return '';
    return status.replace(/_/g, ' ');
  };

  const getEventTitle = (event: TimelineEntry): string => {
    const rawStatus = event.metadata?.newStatus || (event.type === 'status_change' ? event.subType : null);
    if (rawStatus) {
      const statusStr = getStatusString(rawStatus);
      return formatStatusString(candidateService.getStatusDisplayText(statusStr));
    }
    return event.title || event.type;
  };

  const isStatusChangeDescription = (description: string): boolean => {
    if (!description) return false;
    const lowerDesc = description.toLowerCase();
    return lowerDesc.includes('candidate status changed') ||
      lowerDesc.includes('status updated from') ||
      lowerDesc.includes('status changed from');
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px] max-h-[85vh] flex flex-col p-0 border-none shadow-xl rounded-xl bg-white overflow-hidden">
        {/* Minimal Header */}
        <DialogHeader className="px-8 py-6 border-b bg-white">
          <div className="flex flex-col gap-0.5">
            <DialogTitle className="text-xl font-semibold text-gray-900">
              Candidate Timeline
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-500 font-medium">
              Activity for <span className="text-gray-900">{candidateService.getCandidateFullName(candidate)}</span>
            </DialogDescription>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-8 pt-2 pb-6">
          {(!candidate.timeline || candidate.timeline.length === 0) ? (
            <div className="h-full flex flex-col items-center justify-center py-20 text-gray-300">
              <User className="h-8 w-8 opacity-20 mb-3" />
              <p className="text-sm font-medium">No history available</p>
            </div>
          ) : (
            <div className="relative space-y-8 pb-4">
              {/* Minimal Timeline Line */}
              <div className="absolute left-[7px] top-2 bottom-0 w-[1px] bg-gray-100" />

              {timelineEvents.map((event, index) => {
                const title = getEventTitle(event);
                const rawStatus = event.metadata?.newStatus || (event.type === 'status_change' ? event.subType : null);
                const statusStr = getStatusString(rawStatus);
                const statusColor = statusStr ? candidateService.getStatusColor(statusStr) : '#94a3b8';

                const showDescription = event.description && !isStatusChangeDescription(event.description);

                return (
                  <div key={event._id || index} className="relative pl-8 group">
                    {/* Small Minimal Dot */}
                    <div
                      className="absolute left-0 top-1.5 w-[15px] h-[15px] rounded-full border-2 border-white shadow-sm z-10"
                      style={{ backgroundColor: statusColor }}
                    />

                    <div className="flex flex-col">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-2">

                          <h5 className="text-sm font-semibold text-gray-800 tracking-tight">
                            {title}
                          </h5>
                        </div>
                        <span className="text-[11px] text-gray-400 font-medium tabular-nums">
                          {new Date(event.createdAt).toLocaleDateString(undefined, { day: '2-digit', month: 'short' })} • {new Date(event.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>

                      {showDescription && (
                        <p className="text-[13px] text-gray-500 mt-1.5 leading-relaxed">
                          {event.description}
                        </p>
                      )}
                      {/* 
                      {showNotes && (
                        <div
                          className="mt-3 p-3 rounded-lg border border-gray-100 bg-gray-50/30 text-gray-600 text-[13px] italic flex gap-2"
                        >
                          <FileText className="h-4 w-4 shrink-0 mt-0.5 opacity-30" />
                          <span>"{event.notes}"</span>
                        </div>
                      )} */}

                      <div className="mt-3 flex items-center justify-between">
                        {event.createdBy && (
                          <div className="flex items-center gap-1.5">
                            <span className="text-[11px] text-gray-400 font-medium">
                              {event.createdBy.firstName} {event.createdBy.lastName}
                            </span>
                          </div>
                        )}

                        {/* Minimal Metadata */}
                        {event.metadata && (
                          (() => {
                            const filteredKeys = Object.keys(event.metadata).filter(k =>
                              !['previousStatus', 'oldStatus', 'newStatus', 'status', 'updatedVia', 'updatedAt'].includes(k)
                            );
                            if (filteredKeys.length === 0) return null;
                            return (
                              <div className="flex gap-3">
                                {filteredKeys.map(key => (
                                  <span key={key} className="text-[10px] text-gray-400 font-medium lowercase">
                                    <span className="text-gray-300 mr-1">{key}:</span>
                                    {String((event.metadata as any)[key])}
                                  </span>
                                ))}
                              </div>
                            );
                          })()
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CandidateTimelineDialog; 